import { type DetailedHTMLProps, type ElementType, type HTMLAttributes, type Ref } from 'react';

import { cn } from '@/lib/utils';

import { type TextColorType, type TextWeightType, type TitleLevelType } from '../types';

type TitleHTMLType = DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>;

const levelClasses: Record<string, string> = {
  1: 'text-[32px] leading-10',
  2: 'text-[26px] leading-8 tracking-[-0.03em]',
  3: 'text-2xl leading-8',
  4: 'text-[22px] leading-7',
  5: 'text-[18px] leading-6',
  6: 'text-[16px] leading-5',
};

const colorClasses = {
  black: 'text-gray-900',
  green: 'text-green-500',
  red: 'text-red-400',
  white: 'text-white',
  grey: 'text-gray-600',
  'light-green': 'text-primary',
  'dark-grey': 'text-gray-900',
  'dark-green': 'text-green-700',
};

const weightClasses = {
  'extra-light': 'font-extralight',
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  'semi-bold': 'font-semibold',
  bold: 'font-bold',
  'extra-bold': 'font-extrabold',
  'heavy-bold': 'font-black',
};

export type TitleProps = TitleHTMLType & {
  ref?: Ref<HTMLHeadingElement>;
  level: TitleLevelType;
  color?: TextColorType;
  weight?: TextWeightType;
  inline?: boolean;
};

const Title = ({ ref, className, level, color = 'black', weight, inline, ...rest }: TitleProps) => {
  const Element = `h${level}` as ElementType;
  const levelKey = String(level);

  return (
    <Element
      {...rest}
      ref={ref}
      className={cn(
        'font-sans',
        levelClasses[levelKey],
        colorClasses[color],
        weightClasses[weight ?? 'regular'],
        inline && 'inline-block',
        className,
      )}
    />
  );
};

export default Title;
